// This is a class containing constants which are used throughout the application:
// Preferred schemas are JSON/Object representations, but they can be anything.

export const FMV_RANGES = {
    patientCaregiver: {
        advancedExpert: {
            from: 110,
            to: 235
        },
        expert: {
            from: 90,
            to: 165
        },
        individual: {
            from: 55,
            to: 120
        }
    },
    patientOrg: {
        exec: {
            from: 180,
            to: 395
        },
        nonExec: {
            from: 95,
            to: 175
        }
    }
};

export const PARTICIPANT_TYPE_DEFINITIONS = {
    individualPatient: {
        name: 'Individual Patient',
        definition: `A person living with a condition or with a known risk risk for getting a 
        condition who can speak to their individual/personal
        experiences with the disease and related treatments, if applicable.
        May or may not be affiliated with a patient organization but is not speaking on behalf of a patient organization. 
        However, would not be speaking on behalf of a patient
        organization. Payment for the engagement is made directly to the
        individual (or payment is through a third party to the patient)
        `
    },
    individualCaregiver: {
        name: 'Caregiver',
        definition: `A person that can speak to their individual/personal
        experience as a non-professional, non-paid caregiver of
        someone with a condition. This person may or may not also be a
        family member. Payment for the engagement is made directly to the
        individual.
        `
    },
    familyMember: {
        name: 'Family Member',
        definition: `A person who is a family member, related to the
        individual with the illness (e.g., sibling, parent), who can speak to their personal
        experience living with someone with the condition. Payment for engagement is
        made directly to the individual.
        `
    },
    patientGroupRepresentative: {
        name: 'Patient Group Representative',
        definition: `An individual employed by a patient organization,
        typically a nonprofit group that focuses on a condition and/or advocates on
        behalf of patients. Payment for engagement is made typically to the employer 
        organization, not the individual (please see our Compensation Principles for more details).
        `
    },
};

export const CAREGIVER_DEFINITIONS = {
    patientWithConfirmedDiagnosis: {
        name: 'For a patient with a condition, diagnosis confirmed',
        definition: `Patient cared for must hve a diagnosis that is confirmed by a qualified healthcare professional
        `
    },
    patientWithSelfReportedDiagnosis: {
        name: 'For a patient with condition, self-reported diagnosis',
        definition: `Patient cared for can have a diagnosis that is self reported
        `
    },
    patientAtRiskForCondition: {
        name: 'For a patient at risk for the condition',
        definition: `Patient cared for does not yet have the condition but is at risk for the condition
        `
    },
    patientInRemissionOrCured: {
        name: 'For a patient in remission/cured',
        definition: `Patient previously underwent treatment for disease,
        currently no signs of active disease (definition of remission may be disease-specific)
        `
    },
};

export const PATIENT_DEFINITIONS = {
    patientWithConfirmedDiagnosis: {
        name: 'Patient with a condition, diagnosis confirmed',
        definition: `Diagnosis has been confirmed by a qualified health care professional
        `
    },
    patientWithSelfReportedDiagnosis: {
        name: 'Patient with condition, self-reported diagnosis',
        definition: `Diagnosis can be self-reported
        `
    },
    patientAtRiskForCondition: {
        name: 'Patient at risk for the condition',
        definition: `Does not yet have the condition, but is at risk for the condition
        (e.g., genetic testing, family history)
        `
    },
    patientInRemissionOrCured: {
        name: 'Patient in remission/cured',
        definition: `Previously underwent treatment for disease,
        currently no signs of active disease (definition of remission may be disease-specific)
        `
    },
};

export const TRAVEL_TIME_GUIDANCE = {
    noTravel: {
        name: 'No Travel',
        definition: [{
            distance: `No travel is required, e.g., telephone, web interaction`,
            hours: 0
        }]
    },
    localTravel: {
        name: 'Local Travel (1 to 200 miles)',
        definition: [{
            distance: `1 to 200 miles`,
            hours: 2
        }]
    },
    longDistanceTravel: {
        name: 'Long Distance Travel',
        definition: [
            {
                distance: `201 to 500 miles`,
                hours: 4
            },
            {
                distance: `501 to 1500 miles`,
                hours: 6
            },
            {
                distance: `Over 1500 miles`,
                hours: 8
            },
        ]
    },
};

export const PERCENTAGE_RANGES = {
    "zero": { value: '0%', display: '0%' },
    "25th": { value: '25%', display: '25%' },
    "50th": { value: '50% (Recommended)', display: '50% (Recommended)' },
    "75th": { value: '75%', display: '75%' },
    "100th": { value: '100%', display: '100%' }
};

export const LIVING_WITH_RISK_EXPERTISE = {
    id: 'livingWithRisk',
    name: 'Living with or at risk for the condition',
    definition: `
    - Personal/individual experience living with a known risk for a condition. <br><br>
    - Personal/individual experience living with a condition and the treatments. <br><br>
    - Personal/individual experience caring for someone with the condition and treatments. <br><br>
    - Personal/individual experience living with someone with the condition and treatments. <br><br>
    People who have this expertise may say: <br><br>
    - I experienced… <br><br>
    - The person I provide care for experienced… <br><br>
    `
};
export const CAREGIVER_EXPERTISE = {
    id: 'livingWithRisk',
    name: 'Caregiver to someone living with or at risk for the condition',
    definition: `
    - Personal/individual experience living with a known risk for a condition. <br><br>
    - Personal/individual experience living with a condition and the treatments. <br><br>
    - Personal/individual experience caring for someone with the condition and treatments. <br><br>
    - Personal/individual experience living with someone with the condition and treatments. <br><br>
    People who have this expertise may say: <br><br>
    - I experienced… <br><br>
    - The person I provide care for experienced… <br><br>
    `
};

export const FAMILY_MEMBER_EXPERTISE = {
    id: 'livingWithRisk',
    name: 'Family member to someone living with or at risk for the condition',
    definition: `
    - Personal/individual experience living with a known risk for a condition. <br><br>
    - Personal/individual experience living with a condition and the treatments. <br><br>
    - Personal/individual experience caring for someone with the condition and treatments. <br><br>
    - Personal/individual experience living with someone with the condition and treatments. <br><br>
    People who have this expertise may say: <br><br>
    - I experienced… <br><br>
    - The person I provide care for experienced… <br><br>
    `
}

export const EXPERTISE_OPTIONS = {
    knowledgeOfCondition: {
        name: 'Knowledge about the condition beyond individual/personal experience',
        definition: `Knowledge, experience or expertise on the entire population, subpopulations,
        <br> and/or subgroups in terms of experiences and other characteristics. <br><br>
        People who have this expertise may say: <br><br>
         - People who have this illness typically experience…
`
    },
    subjectMatterExpert: {
        name: 'Subject Matter Expert',
        definition: `
        - Expertise on a specific subject such as clinical trials, epidemiology, policy, reimbursement,
        etc gained through lived or career experience or formal training. <br><br>
        - Expert/experienced facilitator
        `
    },
    communications: {
        name: 'Skilled in Public Speaking',
        definition: 'Experience with speaking engagements, addressing small or large groups, addressing the media, facilitation, etc.'
    }
};

export const PGR_ATTRIBUTES = {
    hasSeniorLeadership: {
        name: `Senior Leader`,
        definition: `Does the Patient Group Representative have experience
        as a senior leader in a patient organization (e.g., Chief Executive Officer, Chief Medical Officer, Vice President, etc)`
    },
};

export const MODIFIERS = {
    riskOrLiability: {
        name: 'Risk or Liability:',
        definition: `
        The activity places the participant at some level of risk (e.g. legal or financial),
        in terms of their responsibility (e.g., member of a data safety monitoring board)
        `
    },
    wagesLost: {
        name: 'Wages lost',
        definition: `
        Since the individual is being compensated for their time commitment for the activity,
        it would be unusual to compensate them for wages lost. 
        However, this might be a consideration if it impacts recruitment of representative target population.
        This should be considered on a case-by-case basis.
        `
    },
    childOrElderNeeded: {
        name: 'Care Support',
        definition: `
        To ensure the representativeness of a target population,
        it may be necessary to offer child and elder care reimbursement for a dependent (e.g., childcare or eldercare needed). This should be considered on a case-by-case basis.
        `
    },
    organizationSize: {
        name: 'Size of Patient Organization',
        definition: `
        The size of the organization may also be a factor to consider on a case by case basis.
        `
    },
    eventTimeHorizon: {
        name: 'Urgency (e.g., short time to prepare)',
        definition: `
        The activity is expected to be completed within a very short turnaround time period,
        e.g., the individual is expected to quickly arrange travel, send feedback on materials,
        or create something for a third party, etc. with a very short deadline.
        `
    }
};

export const TRAVEL_REQUIREMENTS = {
    caregiverMustAccompany: {
        name: 'A Caregiver Must Accompany',
        definition: ``
    },
    travelsWithAnimal: {
        name: 'Travels with a Service Animal',
        definition: ``
    },
    additionalDaysNeeded: {
        name: 'Additional 1 - 2 Days of Travel Needed',
        definition: ``
    },
    hasMedicalEquipment: {
        name: 'Accompanying Medical Equipment',
        definition: ``
    },
    specialDietaryNeeds: {
        name: 'Special Dietary Requirements',
        definition: ``
    },
    restBreaksNeeded: {
        name: 'Rest Breaks Needed',
        definition: ``
    },
    otherSpecial: {
        name: 'Other',
        definition: ``
    }
};

export const INTERACTION_MODES = {
    inPerson: {
        name: 'In Person',
        definition: ''
    },
    telephone: {
        name: 'Telephone',
        definition: ''
    },
    teleconference: {
        name: 'Virtual Meeting',
        definition: ''
    },
    webMeeting: {
        name: 'Web-Based',
        definition: ''
    },
    paperBasedMail: {
        name: 'Paper / Mail',
        definition: ''
    },
    other: {
        name: 'Other',
        definition: ''
    },
};
